import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  EmptyState,
  ErrorState,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';
import type { SurveyRow } from './Relations';

interface Band {
  label: string;
  kind: string;
}

interface SurveyView extends SurveyRow {
  questions: { key: string; text: string }[];
  scale: number;
  minSample: number;
  overall: number | null;
  overallBand: Band | null;
  reason: string | null;
  perQuestion: {
    key: string;
    text: string;
    count: number;
    average: number | null;
    band: Band | null;
  }[];
  departments: {
    department: string;
    count: number;
    average: number | null;
    band: Band | null;
    reason: string | null;
  }[];
  comments: string[];
}

/**
 * نتائجُ استبيان رضًا.
 *
 * ## ولماذا تُحجب الأرقامُ تحت العتبة
 *
 * الإجابةُ بلا اسم، والعتبةُ هي ما يجعل ذلك صحيحًا: متوسّطُ قسمٍ فيه ثلاثةٌ
 * اسمٌ لثلاثة، ومن يعرف عددَهم يعرف من قال ماذا. وبلا عتبةٍ يُجاب على
 * الاستبيان مرّةً ثمّ لا يجيب أحدٌ بصدقٍ بعدها — فيصير قياسُ الرضا قياسًا
 * لما يظنّ الناسُ أنّه يُرضي.
 *
 * ## والتعليقاتُ تُخلط
 *
 * عرضُها بترتيب الإجابة يجعل من يعرف من أجاب أوّلًا يعرف من كتب أوّلَ
 * تعليق.
 */
export function PulseSurveyPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const survey = useQuery({
    queryKey: ['relations', 'survey', id],
    queryFn: () => api<SurveyView>(`/hr/relations/surveys/${id}`),
  });

  const setState = useMutation({
    mutationFn: (isOpen: boolean) =>
      api(`/hr/relations/surveys/${id}/state`, {
        method: 'POST',
        body: { isOpen },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const s = survey.data;

  return (
    <div className="page">
      <PageHead
        title={s?.title ?? 'استبيان رضًا'}
        subtitle={
          s
            ? `فُتح ${date(s.opensOn ?? '')} · ${plural(s.responses, WORDS.record)}`
            : undefined
        }
        actions={
          s && (
            <button
              className={s.isOpen ? 'btn' : 'btn btn-primary'}
              disabled={setState.isPending}
              onClick={() => setState.mutate(!s.isOpen)}
            >
              {s.isOpen ? 'أغلقه' : 'افتحه'}
            </button>
          )
        }
      />

      <Link className="link tiny" to="/hr/relations">
        <Icon name="arrowLeft" size={12} /> علاقات الموظفين
      </Link>

      {survey.isError && (
        <ErrorState
          error={survey.error}
          onRetry={() => void survey.refetch()}
        />
      )}
      {survey.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {s && (
        <>
          <div className="stats mt-3">
            <Stat
              label="الحالة"
              value={
                <Badge kind={s.isOpen ? 'info' : 'neutral'}>
                  {s.isOpen ? 'مفتوح' : 'مغلق'}
                </Badge>
              }
              note={s.isOpen ? 'يُجاب عليه الآن' : 'لا يُجاب عليه'}
            />
            <Stat label="الإجابات" value={num(s.responses)} />
            <Stat
              label="المتوسّط العام"
              value={
                s.overall === null ? '—' : `${num(s.overall)} / ${num(s.scale)}`
              }
              note={s.overallBand?.label ?? undefined}
            />
            <Stat label="الأسئلة" value={num(s.questionCount)} />
          </div>

          {s.reason && (
            <Notice title="لا أرقامَ بعد" icon="info">
              {s.reason}
            </Notice>
          )}

          {s.notes && (
            <Notice title="ما يُقال لمن يجيب" icon="note">
              {s.notes}
            </Notice>
          )}

          {/* ---------- السؤال بسؤال ---------- */}
          <div className="card mt-3">
            <h2 className="card-title">السؤال بسؤال</h2>
            <div className="table-wrap mt-2">
              <table className="table">
                <thead>
                  <tr>
                    <th>السؤال</th>
                    <th>أجاب</th>
                    <th>المتوسّط</th>
                    <th>المرتبة</th>
                  </tr>
                </thead>
                <tbody>
                  {s.perQuestion.map((q) => (
                    <tr key={q.key}>
                      <td>{q.text}</td>
                      <td className="num">{num(q.count)}</td>
                      <td className="num">
                        {q.average === null ? '—' : num(q.average)}
                      </td>
                      <td>
                        {q.band ? (
                          <Badge kind={q.band.kind}>{q.band.label}</Badge>
                        ) : (
                          <span className="tiny mute">تحت العتبة</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---------- بالأقسام ---------- */}
          {s.departments.length > 0 && (
            <div className="card mt-3">
              <h2 className="card-title">بالأقسام</h2>
              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>القسم</th>
                      <th>أجاب</th>
                      <th>المتوسّط</th>
                      <th>المرتبة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.departments.map((d) => (
                      <tr key={d.department}>
                        <td>{d.department}</td>
                        <td className="num">{num(d.count)}</td>
                        <td className="num">
                          {d.average === null ? '—' : num(d.average)}
                        </td>
                        <td>
                          {d.band ? (
                            <Badge kind={d.band.kind}>{d.band.label}</Badge>
                          ) : (
                            <span className="tiny mute">{d.reason}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="tiny mute mt-2">
                لا يُعرض متوسّطُ قسمٍ تحت {num(s.minSample)} إجابات: متوسّطُ
                ثلاثةٍ اسمٌ لثلاثة، ومن يعرف عددَهم يعرف من قال ماذا.
              </p>
            </div>
          )}

          {/* ---------- التعليقات ---------- */}
          <div className="card mt-3">
            <h2 className="card-title">ما كُتب حرًّا</h2>
            {s.comments.length === 0 ? (
              <EmptyState
                title="لا تعليقات"
                text={
                  s.reason
                    ? 'لا تُعرض التعليقاتُ تحت العتبة — تعليقُ واحدٍ من ثلاثةٍ يُعرف صاحبُه.'
                    : 'لم يكتب أحدٌ شيئًا حرًّا.'
                }
              />
            ) : (
              <>
                <div className="mt-2">
                  {s.comments.map((text, i) => (
                    <p
                      key={i}
                      className="mb-2"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {text}
                    </p>
                  ))}
                </div>
                <p className="tiny mute mt-2">
                  تُعرض مخلوطةً لا بترتيب الإجابة: الترتيبُ وحدَه يكشف من كتب
                  ماذا لمن يعرف من أجاب أوّلًا.
                </p>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
